import React from 'react'
import BaselineBar from '../detail/timeline/BaselineBar'
import ConstraintPin from '../detail/timeline/ConstraintPin'
import DependencyLink from '../detail/timeline/DependencyLink'
import MilestoneMarker from '../detail/timeline/MilestoneMarker'
import SummaryBar from '../detail/timeline/SummaryBar'
import TaskBar from '../detail/timeline/TaskBar'
import TodayLine from '../detail/timeline/TodayLine'
import { HEADER_H, HEADER_TIER_H, ROW_H } from '../lib/constants'
import type { ISODate } from '../lib/format'
import { barBox, dayIndex, rowCenterY, rowY, xOf, type DateRange } from '../lib/geometry'
import { headerTiers, type Tick } from '../lib/header-tiers'
import { routeLink, type Endpoint } from '../lib/link-routing'
import type { PrintEndpoint, PrintLink, PrintRow } from './print-data'
import type { PrintLabels } from './print-labels'
import type { PrintLayout, PrintPageRef } from './print-layout'
import type { PrintOptions } from './print-options'
import { textWidth } from './print-text'

export interface PrintTimelineProps {
  layout: PrintLayout
  page: PrintPageRef
  options: PrintOptions
  /** Already sliced to this page — row `i` draws at `rowY(i)`. */
  rows: PrintRow[]
  /** Absolute visible-row indices; endpoints off this page are drawn out of bounds and clipped. */
  links: PrintLink[]
  range: DateRange
  today: ISODate
  statusDate: ISODate | null
  labels: PrintLabels
  locale: string
}

/** Space a pinned label needs beyond its own text before it would touch the next tick's label. */
const LABEL_CLEARANCE = 8

/** Header cell, same shape as `GanttTimelineHeader`'s `Tick` minus the per-tick clip: the strip
 * clip below already cuts the tier at the page edge (spec §4.3). `sticky` clamps the label to the
 * strip's left edge the way the screen's header clamps it to `scrollLeft` — without it the first
 * top-tier tick (which always starts before the strip: a year/month begins before the padded range
 * start) would print with no label at all.
 *
 * A pinned label is drawn on the tick's *visible remainder*, which can be far narrower than the
 * tick itself: at week zoom a strip starting near a month's end left `January 2024` 33 units in
 * which to draw 85 units of text, printing it straight over `February 2024`. There is no scrolling
 * to reveal it later, so a label that cannot fit its remainder is dropped; the tick rect stays. */
const tickCell = (tk: Tick, y: number, sticky?: number): React.ReactNode => {
  const pinnedX = sticky == null ? tk.x : sticky
  const fits = sticky == null || tk.x + tk.width - pinnedX >= textWidth(tk.label) + LABEL_CLEARANCE
  return (
    <g key={tk.key}>
      <rect x={tk.x} y={y} width={Math.max(0, tk.width)} height={HEADER_TIER_H} className="fill-none stroke-border" />
      {fits && (
        <text x={pinnedX + 4} y={y + HEADER_TIER_H / 2} dominantBaseline="middle" className="text-xs fill-muted-foreground">{tk.label}</text>
      )}
    </g>
  )
}

/**
 * The timeline pane on paper: the screen's own tiers, gridlines, bars, baselines, constraint pins,
 * today/status lines and dependency links (spec §4.3), reusing `detail/timeline/*` unchanged —
 * never copied — with every interactive affordance switched off (`selected`, `showHandles`, no
 * handlers). One horizontal strip of days per page; the strip clip cuts everything that leaves it.
 */
const PrintTimeline: React.FC<PrintTimelineProps> = ({ layout, page, options, rows, links, range, today, statusDate, labels, locale }) => {
  const strip = layout.strips[page.strip]
  const pxPerDay = layout.pxPerDay
  const dx = strip.gridWidth - strip.fromDay * pxPerDay
  const bodyH = HEADER_H + rows.length * ROW_H
  const clipId = `print-tl-${page.index}`
  const linkClipId = `print-links-${page.index}`
  // Ticks and gridlines run through the trailing-label gutter too, so the space right of the last
  // bar shows dates instead of a blank strip (Freelo #32277855, mirrored from the screen).
  const tiers = headerTiers(layout.zoomForTiers, range.start, strip.fromDay, strip.toDay - 1 + layout.gutterDays, pxPerDay, locale)
  const lineFrom = strip.fromDay * pxPerDay
  const lineTo = lineFrom + strip.timelineWidth
  const inStrip = (day: number): boolean => day >= strip.fromDay && day < strip.toDay
  const statusDay = statusDate ? dayIndex(statusDate, range.start) : -1

  const endpointOf = (e: PrintEndpoint, absoluteRow: number): Endpoint => {
    const box = barBox(e, range.start, pxPerDay)
    return { x1: box.x, x2: box.x + Math.max(2, box.width), y: rowCenterY(absoluteRow - page.rowFrom) }
  }
  const pageLinks = options.includeLinks
    ? links.filter((l) => Math.max(l.fromRow, l.toRow) >= page.rowFrom && Math.min(l.fromRow, l.toRow) < page.rowTo)
    : []

  return (
    <g data-print-timeline>
      <clipPath id={clipId}>
        <rect x={strip.gridWidth} y={0} width={strip.timelineWidth} height={bodyH} />
      </clipPath>
      {/* A link to a row on another page is routed to `rowY(-1)` or past the last row on purpose,
          so the line visibly leaves the page. The strip clip alone still lets the run above row 0
          paint straight through the two tier bands and strike out the tick labels, so links get a
          second clip that starts below the header. Bars never leave their own row and do not. */}
      <clipPath id={linkClipId}>
        <rect x={strip.gridWidth} y={HEADER_H} width={strip.timelineWidth} height={Math.max(0, bodyH - HEADER_H)} />
      </clipPath>
      <g clipPath={`url(#${clipId})`}>
        <g transform={`translate(${dx},0)`}>
          {tiers.top.map((tk) => tickCell(tk, 0, Math.max(tk.x, lineFrom)))}
          {tiers.bottom.map((tk) => tickCell(tk, HEADER_TIER_H))}
          {tiers.bottom.map((tk) => (
            <line key={`grid-${tk.key}`} x1={tk.x} x2={tk.x} y1={HEADER_H} y2={bodyH} className="stroke-border" strokeWidth={1} />
          ))}
          {rows.map((r, i) => (
            <line key={`row-${r.id}`} x1={lineFrom} x2={lineTo} y1={rowY(i) + ROW_H} y2={rowY(i) + ROW_H} className="stroke-border" strokeWidth={1} />
          ))}
          {inStrip(dayIndex(today, range.start)) && (
            <TodayLine x={xOf(today, range.start, pxPerDay) + pxPerDay / 2} y1={HEADER_H - 6} y2={bodyH} label={labels.today} />
          )}
          {statusDate && inStrip(statusDay) && (
            <TodayLine dashed x={xOf(statusDate, range.start, pxPerDay) + pxPerDay / 2} y1={HEADER_H - 6} y2={bodyH} />
          )}

          {/* This block composes the same bars as `detail/ActivityBar.tsx` — baseline, then summary /
              milestone / task, then the constraint pin — and the two must stay in sync. It is not
              reused: `ActivityBar` calls the gantt store and the hover hooks, which do not exist
              under `renderToStaticMarkup` or in the off-screen PDF host. */}
          {rows.map((r, i) => {
            const a = r.activity
            const top = rowY(i)
            const box = barBox(a, range.start, pxPerDay)
            const critical = options.includeCritical && a.isCritical
            return (
              <g key={a.id} data-activity-id={a.id}>
                {options.includeBaseline && a.baselineStart && a.baselineFinish && (
                  <BaselineBar
                    {...barBox({ start: a.baselineStart, finish: a.baselineFinish, isMilestone: a.isMilestone }, range.start, pxPerDay)}
                    rowTop={top}
                    isMilestone={a.isMilestone}
                    isSummary={a.isSummary}
                  />
                )}
                {a.isSummary ? (
                  <SummaryBar x={box.x} width={box.width} rowTop={top} label={a.name} percent={a.rolledPercent} selected={false} shiftUp={options.includeBaseline} />
                ) : a.isMilestone ? (
                  <MilestoneMarker
                    x={box.x} rowTop={top} label={a.name}
                    critical={critical} completed={!!a.actualFinish} selected={false} shiftUp={options.includeBaseline}
                    activityId={a.id} showHandles={false}
                  />
                ) : (
                  <TaskBar
                    x={box.x} width={box.width} rowTop={top} label={a.name}
                    percent={a.actualFinish ? 100 : a.percentComplete}
                    critical={critical} selected={false} shiftUp={options.includeBaseline}
                    activityId={a.id} showHandles={false}
                  />
                )}
                {a.constraintType !== 'ASAP' && <ConstraintPin x={box.x} rowTop={top} />}
              </g>
            )
          })}

        </g>

        {/* Same `translate` as the body group, but under the header-excluding clip. */}
        <g clipPath={`url(#${linkClipId})`}>
          <g transform={`translate(${dx},0)`}>
            {pageLinks.map((l) => (
              <DependencyLink
                key={l.id}
                path={routeLink(l.type, endpointOf(l.from, l.fromRow), endpointOf(l.to, l.toRow))}
                critical={l.critical}
                selected={false}
                hovered={false}
              />
            ))}
          </g>
        </g>
      </g>
    </g>
  )
}

export default PrintTimeline
